import { Modal, Select, TextInput, Group } from '@mantine/core'
import { useForm } from '@mantine/form'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import DateShortcutButton from 'src/components/Inputs/DateShortcutButton/DateShortcutButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_DOCUMENT } from 'src/graphql/documents.graphql'
import { GET_STEP_OPTIONS } from 'src/graphql/steps.graphql'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import { DQF_STEP_TYPES, StepType } from 'src/graphql/types/steps'
import useVoEDocumentCompletionGate from 'src/hooks/useVoEDocumentCompletionGate/useVoEDocumentCompletionGate'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import { formatSnakeValue, getStepLabel } from 'src/lib/formatters'
import { sortStepsByTemplate } from 'src/lib/stepOrder'

const categories = buildDocumentCategoryOptions(formatSnakeValue)
const statuses = Object.values(DocumentUserStatus)

const STEP_OPTIONS_PAGE_INFO = { offset: 0, limit: 100 }

const EditDocumentModal = ({
  document,
  applicantId,
  isOpen,
  onRender,
  onClose,
}) => {
  const initialStepId = document.step?.stepId ?? null

  const form = useForm({
    initialValues: {
      category: document.category,
      userStatus: document.userStatus,
      fileName: document.fileName,
      expirationDate: document.expirationDate
        ? new Date(document.expirationDate)
        : null,
      stepId: initialStepId,
    },
  })

  const { data: stepOptionsData } = useQuery(GET_STEP_OPTIONS, {
    variables: {
      applicantId,
      stepTypes: [...DQF_STEP_TYPES, 'CUSTOM' as StepType],
      pageInfo: STEP_OPTIONS_PAGE_INFO,
    },
    skip: !applicantId,
  })
  const stepOptions = sortStepsByTemplate(
    stepOptionsData?.stepsGroupByType?.items ?? [],
    null
  ).map((step) => ({
    value: step.stepId,
    label: getStepLabel(step),
  }))

  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: onRender,
    onCompleted: () => {
      toast('Document updated successfully', 'success')
      onClose()
    },
    onError: () => {
      toast('Unable to update document, please try again.', 'error')
    },
  })

  const { requestStatusChange, modal: voeCompletionModal } =
    useVoEDocumentCompletionGate()

  const onFormSubmit = (values) => {
    requestStatusChange({
      voe: document.voe,
      userStatus: values.userStatus,
      currentStatus: document.userStatus,
      send: (voeCompletion) =>
        updateDocument({
          variables: {
            id: document.documentId,
            input: {
              expirationDate: values.expirationDate
                ? new Date(values.expirationDate)
                : null,
              fileName: values.fileName,
              userStatus: values.userStatus,
              category: values.category,
              ...(voeCompletion && { voeCompletion }),
              // Only send stepId when the user actually changed it — an explicit
              // stepId takes precedence over category-driven step linking.
              ...(values.stepId !== initialStepId && {
                stepId: values.stepId ?? null,
              }),
            },
          },
        }),
      // Keeping the current status puts the select back; nothing is saved.
      onCancel: () => form.setFieldValue('userStatus', document.userStatus),
    })
  }

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        classNames={{ title: 'text-lg font-bold' }}
        title="Edit document"
        centered
      >
        <div className="flex flex-col gap-6">
          <form onSubmit={form.onSubmit((values) => onFormSubmit(values))}>
            <div className="flex-row flex-col">
              <div className="grid grid-cols-2 gap-4">
                <TextInput
                  required
                  className="col-span-2"
                  placeholder={
                    form.getInputProps('file').value
                      ? form.getInputProps('file').value.name
                      : 'Enter a file name'
                  }
                  label="File name"
                  {...form.getInputProps('fileName')}
                />
                <Select
                  withAsterisk
                  label="Category"
                  placeholder="Select category"
                  data={categories}
                  searchable
                  required
                  {...form.getInputProps('category')}
                />

                <Select
                  withAsterisk
                  label="Status"
                  placeholder="Select status"
                  data={statuses.map((status) => ({
                    label: formatSnakeValue(status),
                    value: status,
                  }))}
                  required
                  {...form.getInputProps('userStatus')}
                />
                <div className="flex flex-col">
                  <DateInput
                    label="Expiration Date"
                    clearable
                    value={form.values.expirationDate}
                    onChange={(date) =>
                      form.setFieldValue('expirationDate', date)
                    }
                  />
                  <DateShortcutButton
                    onClick={(_event, date) =>
                      form.setFieldValue('expirationDate', date)
                    }
                  />
                </div>
                {applicantId && (
                  <Select
                    label="Checklist item"
                    placeholder="None"
                    data={stepOptions}
                    searchable
                    clearable
                    value={form.values.stepId}
                    onChange={(value) => form.setFieldValue('stepId', value)}
                  />
                )}
              </div>
              <Group
                justify="flex-end"
                mt="md"
                className="grid grid-cols-2 gap-4 border-t py-4"
              >
                <Button text="Cancel" variant="outline" onClick={onClose} />
                <Button
                  type="submit"
                  disabled={!form.isValid()}
                  text="Save"
                  variant="filled"
                />
              </Group>
            </div>
          </form>
        </div>
      </Modal>
      {voeCompletionModal}
    </>
  )
}

export default EditDocumentModal
